import React from 'react'
import { Skeleton } from 'antd'

import styles from './ProjectRoadMap.module.css'
import skStyles from './ProjectRoadMapSkeleton.module.css'

/** Mirrors the widths in ROADMAP_COLUMNS so the skeleton lands where the real
 *  grid will. Keep in step if those change. */
const COLUMN_WIDTHS = [240, 120, 180, 90, 110, 110]

/* Staggered so the placeholder reads as a schedule rather than a block. */
const BAR_LAYOUT = [
    { left: '4%', width: '38%' },
    { left: '18%', width: '26%' },
    { left: '32%', width: '44%' },
    { left: '10%', width: '30%' },
    { left: '46%', width: '22%' },
    { left: '24%', width: '40%' },
    { left: '52%', width: '28%' },
    { left: '12%', width: '34%' },
]

/* Three tiers matching ROADMAP_SCALES (month / week / day). Enough segments to
   fill any width without wrapping — narrow viewports simply overflow the last
   segments off screen, which is exactly what the real timescale does. */
const SCALE_TIERS = [4, 8, 24]

const ProjectRoadMapSkeleton = ({ height, rowHeight, headerHeight, rows }) => {
    const tierHeight = headerHeight / SCALE_TIERS.length

    return (
        <div className={styles.ganttSurface} style={{ height }} aria-hidden>
            <div className={skStyles.header} style={{ height: headerHeight }}>
                {COLUMN_WIDTHS.map((width, index) => (
                    <div
                        key={index}
                        className={skStyles.headerCell}
                        style={{ flex: `0 0 ${width}px` }}
                    >
                        <Skeleton.Input
                            active
                            size="small"
                            block
                            style={{ height: 12, width: '60%' }}
                        />
                    </div>
                ))}

                {/* The chart's own header: three stacked timescale tiers. The
                    real Gantt renders month/week/day here — a blank band
                    reads as broken. */}
                <div className={skStyles.chartHeader}>
                    {SCALE_TIERS.map((segments, tierIndex) => (
                        <div
                            key={tierIndex}
                            className={skStyles.chartHeaderTier}
                            style={{ height: tierHeight }}
                        >
                            {Array.from({ length: segments }).map((_, i) => (
                                <div
                                    key={i}
                                    className={skStyles.chartHeaderSegment}
                                    style={{ width: `${100 / segments}%` }}
                                />
                            ))}
                        </div>
                    ))}
                </div>
            </div>

            {Array.from({ length: rows }).map((_, rowIndex) => {
                const bar = BAR_LAYOUT[rowIndex % BAR_LAYOUT.length]
                return (
                    <div
                        key={rowIndex}
                        className={skStyles.row}
                        style={{ height: rowHeight }}
                    >
                        <div
                            className={skStyles.cell}
                            style={{ flex: `0 0 ${COLUMN_WIDTHS[0]}px` }}
                        >
                            <Skeleton.Input
                                active
                                size="small"
                                block
                                style={{ height: 12 }}
                            />
                        </div>

                        <div
                            className={`${skStyles.cell} ${skStyles.cellCentered}`}
                            style={{ flex: `0 0 ${COLUMN_WIDTHS[1]}px` }}
                        >
                            <Skeleton.Button
                                active
                                size="small"
                                style={{ height: 20, width: 62, minWidth: 62 }}
                            />
                        </div>

                        <div
                            className={skStyles.cell}
                            style={{ flex: `0 0 ${COLUMN_WIDTHS[2]}px` }}
                        >
                            <div className={skStyles.assignee}>
                                <Skeleton.Avatar active size={24} />
                                <Skeleton.Input
                                    active
                                    size="small"
                                    block
                                    style={{ height: 12 }}
                                />
                            </div>
                        </div>

                        {COLUMN_WIDTHS.slice(3).map((width, index) => (
                            <div
                                key={index}
                                className={`${skStyles.cell} ${skStyles.cellCentered}`}
                                style={{ flex: `0 0 ${width}px` }}
                            >
                                <Skeleton.Input
                                    active
                                    size="small"
                                    block
                                    style={{ height: 12, width: '60%' }}
                                />
                            </div>
                        ))}

                        <div className={skStyles.chartCell}>
                            <div
                                className={skStyles.bar}
                                style={{
                                    marginLeft: bar.left,
                                    width: bar.width,
                                }}
                            />
                        </div>
                    </div>
                )
            })}
        </div>
    )
}

export default ProjectRoadMapSkeleton
